/**
 * Shows/hides the "Use transport" checkbox on each traveller card
 * based on whether any Transport offer-price is currently checked.
 *
 * When no transport is selected, all transport checkboxes are hidden
 * and reset to checked so re-enabling doesn't silently drop seats.
 * Calls the provided recalc callback on every relevant change.
 */

export function setupTransportToggle(recalcPrices: () => void): void {
    // Listen for price-checkbox changes anywhere in the document
    document.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        if (target.name !== 'SelectedOfferPriceIds') return;
        _sync(recalcPrices);
    });

    // Initial sync in case the page loads with pre-checked prices (edit flow)
    _sync(recalcPrices);
}

/**
 * Called after a new traveller card is appended so it immediately
 * reflects the current transport-selection state.
 */
export function syncNewTravellerCard(card: HTMLElement, recalcPrices: () => void): void {
    const hasTransport = _hasCheckedTransport();
    _applyToCard(card, hasTransport);
    card.querySelector<HTMLInputElement>('.traveller-uses-transport')?.addEventListener('change', () => {
        _updateWarning();
        recalcPrices();
    });
}

// ── internals ──────────────────────────────────────────────────────────────

function _hasCheckedTransport(): boolean {
    return document.querySelectorAll<HTMLInputElement>(
        'input[name="SelectedOfferPriceIds"][data-offer-group="Transport"]:checked'
    ).length > 0;
}

function _applyToCard(card: HTMLElement, show: boolean): void {
    const wrap = card.querySelector<HTMLElement>('.traveller-transport-wrap');
    if (!wrap) return;
    if (show) {
        wrap.classList.remove('d-none');
    } else {
        wrap.classList.add('d-none');
        const cb = wrap.querySelector<HTMLInputElement>('.traveller-uses-transport');
        if (cb) cb.checked = true; // reset so re-enabling is sane
    }
}

function _updateWarning(): void {
    const warning = document.getElementById('transportSeatWarning');
    if (!warning) return;
    const hasTransport = _hasCheckedTransport();
    if (!hasTransport) {
        warning.classList.add('d-none');
        return;
    }
    const anyChecked = document.querySelectorAll<HTMLInputElement>('.traveller-uses-transport:checked').length > 0;
    warning.classList.toggle('d-none', anyChecked);
}

function _updateSubmitGuard(): void {
    const btn = document.getElementById('createContractBtn') as HTMLButtonElement | null;
    if (!btn) return;
    const hasTransport = _hasCheckedTransport();
    if (!hasTransport) {
        btn.disabled = false;
        return;
    }
    const anyChecked = document.querySelectorAll<HTMLInputElement>('.traveller-uses-transport:checked').length > 0;
    btn.disabled = !anyChecked;
}

function _sync(recalcPrices: () => void): void {
    const hasTransport = _hasCheckedTransport();
    document.querySelectorAll<HTMLElement>('.traveller-card').forEach(card => {
        _applyToCard(card, hasTransport);
    });
    _updateWarning();
    _updateSubmitGuard();
    recalcPrices();
}
